/**
 * Pure translation layer: Material React Table v3 column defs -> MUI X
 * DataGridPremium `GridColDef`s, plus the row-id helpers.
 *
 * Kept framework-light and side-effect-free so it can be unit-tested in
 * isolation (see tableColumnAdapter.test.js). The only React dependency is
 * `isValidElement` for detecting JSX headers/cells.
 */
import { isValidElement } from 'react';
import {
  gridStringOrNumberComparator,
  gridNumberComparator,
  gridDateComparator
} from '@mui/x-data-grid-premium';

// Non-enumerable synthetic id we attach to rows that have neither `_id` nor
// `id`, so DataGrid's mandatory getRowId never throws. Non-enumerable so it
// does not leak into `{...row}` spreads that consumers forward downstream.
export const ROW_ID_KEY = '__mui_id__';
export const ROW_NUMBER_FIELD = '__row_number__';

/**
 * Attach a stable id to every row. Rows that already carry `_id`/`id` are
 * returned untouched (referential identity preserved); rows without either
 * get a non-enumerable `ROW_ID_KEY` set to their index. Primitive rows are
 * wrapped. Mirrors MRT's implicit row indexing.
 */
export function injectRowIds(rows) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  return rows.map((row, index) => {
    if (row == null || typeof row !== 'object') {
      return { value: row, [ROW_ID_KEY]: `__idx_${index}` };
    }
    const natural = row._id ?? row.id;
    // Unique natural id → leave the row untouched (identity preserved).
    if (natural != null && !seen.has(natural)) {
      seen.add(natural);
      return row;
    }
    // Missing OR duplicate natural id → attach a guaranteed-unique synthetic
    // id. DataGrid throws/misrenders on duplicate ids where MRT tolerated them
    // (e.g. FormBuilder concatenates built-in templates with DB schemas that
    // can share an _id) — this keeps the grid robust.
    let synth = natural != null ? `${natural}__${index}` : `__idx_${index}`;
    while (seen.has(synth)) synth = `${synth}_`;
    seen.add(synth);
    const copy = { ...row };
    Object.defineProperty(copy, ROW_ID_KEY, {
      value: synth,
      enumerable: false,
      configurable: true
    });
    return copy;
  });
}

/**
 * Build the getRowId function DataGrid needs, honouring a consumer override.
 * The default prefers the synthetic `ROW_ID_KEY` (present only on rows that had
 * a missing/duplicate natural id) and otherwise falls back to `_id`/`id`.
 */
export function makeGetRowId(userGetRowId) {
  if (typeof userGetRowId === 'function') return userGetRowId;
  return (row) => row?.[ROW_ID_KEY] ?? row?._id ?? row?.id;
}

/**
 * Reconstruct the argument object MRT hands a column `Cell` renderer from a
 * DataGrid `renderCell` params object, so existing consumer `Cell` functions
 * work unchanged. This is the single biggest source of would-be churn, kept
 * in one place.
 *
 * MRT `Cell({ cell, row, renderedCellValue, column, table })`:
 *  - cell.getValue()  -> params.value       (post-valueGetter, pre-format)
 *  - row.original     -> params.row          (whole row object)
 *  - row.index        -> visible-row index   (page-relative; see note)
 *  - renderedCellValue-> params.formattedValue ?? params.value
 *  - table            -> the GridApi (NOT a real MRT instance; present only so
 *                        Cell fns that poke `table` don't throw)
 */
export function toMrtCellArg(params) {
  const original = params.row;
  const api = params.api;
  return {
    cell: {
      getValue: () => params.value,
      getValueString: () => (params.value == null ? '' : String(params.value)),
      row: { original, id: params.id },
      column: { id: params.field, columnDef: params.colDef }
    },
    row: {
      original,
      id: params.id,
      get index() {
        return api?.getRowIndexRelativeToVisibleRows?.(params.id) ?? 0;
      },
      getValue: (colId) => api?.getCellValue?.(params.id, colId),
      getIsExpanded: () => {
        try {
          return api?.getExpandedDetailPanels?.().has?.(params.id) ?? false;
        } catch {
          return false;
        }
      },
      toggleExpanded: () => api?.toggleDetailPanel?.(params.id)
    },
    renderedCellValue: params.formattedValue ?? params.value,
    column: { id: params.field, columnDef: params.colDef },
    table: api
  };
}

const READ_ROW = (p) => ({
  original: p.api?.getRow?.(p.id),
  getValue: (colId) => p.api?.getCellValue?.(p.id, colId)
});

/** Map an MRT `sortingFn` (function or preset string) to a GridComparatorFn. */
export function resolveComparator(sortingFn) {
  if (typeof sortingFn === 'function') {
    return (v1, v2, p1, p2) => sortingFn(READ_ROW(p1), READ_ROW(p2), p1.field);
  }
  switch (sortingFn) {
    case 'alphanumeric':
    case 'alphanumericCaseSensitive':
    case 'text':
    case 'textCaseSensitive':
      return gridStringOrNumberComparator;
    case 'datetime':
      return gridDateComparator;
    case 'basic':
      return gridNumberComparator;
    default:
      return undefined; // let DataGrid pick a default comparator by type
  }
}

function resolveField(col, index) {
  if (typeof col.accessorKey === 'string' && col.accessorKey.length)
    return col.accessorKey;
  if (col.id != null) return String(col.id);
  if (typeof col.header === 'string' && col.header.length) return col.header;
  return `col_${index}`;
}

/**
 * Translate MRT column defs to GridColDef[]. `defaults` lets the caller tune
 * the fill behaviour (MRT auto-sizes; DataGrid does not) — by default unspec'd
 * columns get `flex:1, minWidth`.
 */
export function buildGridColumns(mrtColumns = [], options = {}) {
  const { defaultMinWidth = 120, flexByDefault = true } = options;
  const usedFields = new Set();

  return (Array.isArray(mrtColumns) ? mrtColumns : []).map((col, index) => {
    let field = resolveField(col, index);
    // DataGrid requires unique fields; de-dupe (e.g. two unnamed actions cols).
    while (usedFields.has(field)) field = `${field}_${index}`;
    usedFields.add(field);

    const gc = {
      field,
      sortable: col.enableSorting !== false,
      // Columns stay `filterable` (default) so the global quick-filter search
      // still matches their values; we simply never surface the per-column
      // filter UI (no filter panel trigger + column menu disabled below),
      // matching the old wrapper's enableColumnFilters={false}/actions=false.
      disableColumnMenu: true,
      headerName: typeof col.header === 'string' ? col.header : field
    };

    // JSX / function headers -> renderHeader (headerName stays a string for
    // the column menu / quick-filter labels).
    if (col.header != null && typeof col.header !== 'string') {
      const header = col.header;
      gc.renderHeader = isValidElement(header) ? () => header : () => header;
      gc.headerName = field;
    }
    if (typeof col.Header === 'function') {
      gc.renderHeader = () => col.Header({ column: { id: field } });
    }

    // Value access: accessorFn, or dot-path accessorKey (DataGrid `field` does
    // NOT deep-resolve nested paths).
    if (typeof col.accessorFn === 'function') {
      const fn = col.accessorFn;
      gc.valueGetter = (value, row) => fn(row);
    } else if (
      typeof col.accessorKey === 'string' &&
      col.accessorKey.includes('.')
    ) {
      const path = col.accessorKey.split('.');
      gc.valueGetter = (value, row) =>
        path.reduce((acc, key) => (acc == null ? acc : acc[key]), row);
    }

    // Sizing. Columns are content-proportioned AND fill the available width
    // via `flex`. The flex weight is the author's `size` hint SCALED to a small
    // range (size/100) so it stays comparable to the default weight of unsized
    // columns (1) — otherwise a single size:300 column would get 300x the
    // weight of an unsized one and squash everything else. The actions column
    // is the exception: fixed width, pinned right, with a minWidth that fits
    // the "Actions" header so it is never truncated.
    const isActionsCol = field === 'actions';
    if (isActionsCol) {
      gc.width = col.size != null ? col.size : 140;
      gc.minWidth = 110;
    } else if (col.size != null) {
      gc.flex = col.size / 100;
      gc.minWidth = Math.max(80, Math.min(col.size, 160));
    } else if (flexByDefault) {
      gc.flex = 1;
      gc.minWidth = defaultMinWidth;
    }
    if (col.minSize != null) gc.minWidth = col.minSize;
    if (col.maxSize != null) gc.maxWidth = col.maxSize;
    // MRT `grow: false` → pin the column to a fixed width (don't flex).
    if (col.grow === false && gc.flex) {
      gc.width = col.size != null ? col.size : gc.minWidth || defaultMinWidth;
      delete gc.flex;
      delete gc.minWidth;
    }

    // Alignment carried on the old mui*CellProps — either as an `align` prop or
    // an `sx.textAlign`. (DataGrid has no per-column sx; we map alignment only.)
    const bodyAlign =
      col.muiTableBodyCellProps?.align ??
      col.muiTableBodyCellProps?.sx?.textAlign;
    if (bodyAlign) gc.align = bodyAlign;
    const headAlign =
      col.muiTableHeadCellProps?.align ??
      col.muiTableHeadCellProps?.sx?.textAlign;
    if (headAlign) gc.headerAlign = headAlign;

    // Cell renderer via the shim.
    if (typeof col.Cell === 'function') {
      const CellFn = col.Cell;
      gc.renderCell = (params) => CellFn(toMrtCellArg(params));
    }

    // Edit cell (native DataGrid editing adopted where a consumer opts in).
    if (typeof col.renderEditCell === 'function') {
      gc.editable = true;
      gc.renderEditCell = col.renderEditCell;
    }

    // Sorting comparator.
    if (col.sortingFn) {
      const cmp = resolveComparator(col.sortingFn);
      if (cmp) gc.sortComparator = cmp;
    }

    // Pass-through of a handful of native GridColDef keys when a migrated
    // consumer supplies them directly (progressive escape hatch).
    for (const key of [
      'type',
      'align',
      'headerAlign',
      'width',
      'flex',
      'minWidth',
      'maxWidth',
      'valueGetter',
      'valueFormatter',
      'renderCell',
      'renderHeader',
      'cellClassName',
      'headerClassName',
      'sortable',
      'filterable',
      'pinnable',
      'editable',
      'disableColumnMenu',
      'getApplyQuickFilterFn',
      'field'
    ]) {
      if (col[key] !== undefined) gc[key] = col[key];
    }

    return gc;
  });
}

// resolveSearchFieldOptions / restrictQuickFilterToFields / NON_SEARCHABLE_FIELDS
// were removed in TGCLOUD-1818 together with the "All fields" search-scope
// picker they served (TGCLOUD-1682). They muted a column's
// getApplyQuickFilterFn so the one search box could be narrowed to chosen
// columns; filter chips express that as real criteria instead. See
// src/components/proband-info/FamilySearchFilters/.
